import React from 'react';

const RealResultsSection = () => (
  <div className="py-12 bg-white dark:bg-gray-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
          Real Results from Australian Manufacturers Like You
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-4xl mx-auto">
          See how Australian businesses have achieved significant energy savings and improved
          their bottom line with quantified savings, ROI, and CO₂ reductions through our
          professional assessments.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Food Processing Card */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 dark:from-slate-900 dark:to-black p-8 rounded-2xl text-white border border-slate-700 dark:border-slate-800">
          <div className="text-emerald-400 dark:text-emerald-300 font-medium mb-1">
            Food Processing
          </div>
          <div className="text-gray-400 dark:text-gray-500 text-sm mb-6">Australia</div>
          <h3 className="text-2xl font-bold mb-6 text-white">Major Food Manufacturer</h3>

          <div className="mb-6">
            <h4 className="font-semibold text-white mb-3">Challenge</h4>
            <p className="text-gray-300 dark:text-gray-400 text-sm leading-relaxed">
              High energy consumption from refrigeration systems, inefficient lighting, and poor
              power factor management.
            </p>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold text-white mb-3">ESA Solution</h4>
            <p className="text-gray-300 dark:text-gray-400 text-sm leading-relaxed">
              Comprehensive energy efficiency upgrade including variable speed drives, LED
              lighting, and power factor correction.
            </p>
          </div>

          <div className="border-t border-gray-700 dark:border-gray-800 pt-6">
            <h4 className="font-semibold text-emerald-400 dark:text-emerald-300 mb-4">
              Results Achieved
            </h4>
            <div className="mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-400 dark:text-emerald-300 mb-2">
                  $230,400
                </div>
                <div className="text-sm text-gray-400 dark:text-gray-500 mb-3">
                  Annual Cost Savings
                </div>
                <div className="text-lg font-semibold text-emerald-300 dark:text-emerald-400">
                  2.3 years
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-600">Payback Period</div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="bg-slate-800/50 dark:bg-black/30 rounded-lg p-4 border-l-4 border-emerald-400 dark:border-emerald-300">
              <p className="text-gray-300 dark:text-gray-400 text-sm italic">
                "The analysis was incredibly detailed and gave us a clear roadmap to reduce our
                energy costs. We're already seeing the projected savings."
              </p>
              <p className="text-emerald-400 dark:text-emerald-300 text-xs mt-2 font-medium">
                — Operations Manager, Major Food Manufacturer
              </p>
            </div>
          </div>
        </div>

        {/* Packaging Manufacturing Card */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 dark:from-slate-900 dark:to-black p-8 rounded-2xl text-white border border-slate-700 dark:border-slate-800">
          <div className="text-emerald-400 dark:text-emerald-300 font-medium mb-1">
            Packaging Manufacturing
          </div>
          <div className="text-gray-400 dark:text-gray-500 text-sm mb-6">Australia</div>
          <h3 className="text-2xl font-bold mb-6 text-white">Packaging Manufacturer</h3>

          <div className="mb-6">
            <h4 className="font-semibold text-white mb-3">Challenge</h4>
            <p className="text-gray-300 dark:text-gray-400 text-sm leading-relaxed">
              Inefficient compressed air systems, outdated lighting, and poor power factor
              management.
            </p>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold text-white mb-3">ESA Solution</h4>
            <p className="text-gray-300 dark:text-gray-400 text-sm leading-relaxed">
              VSD on air compressors, LED retrofit with smart controls, power factor correction,
              and leak detection.
            </p>
          </div>

          <div className="border-t border-gray-700 dark:border-gray-800 pt-6">
            <h4 className="font-semibold text-emerald-400 dark:text-emerald-300 mb-4">
              Results Achieved
            </h4>
            <div className="mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-400 dark:text-emerald-300 mb-2">
                  $280K
                </div>
                <div className="text-sm text-gray-400 dark:text-gray-500 mb-3">
                  Annual Cost Savings
                </div>
                <div className="text-lg font-semibold text-emerald-300 dark:text-emerald-400">
                  2.1 years
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-600">Payback Period</div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="bg-slate-800/50 dark:bg-black/30 rounded-lg p-4 border-l-4 border-emerald-400 dark:border-emerald-300">
              <p className="text-gray-300 dark:text-gray-400 text-sm italic">
                "The compressed air system recommendations alone saved us $120K annually. ROI was
                even better than projected."
              </p>
              <p className="text-emerald-400 dark:text-emerald-300 text-xs mt-2 font-medium">
                — Facilities Manager, Packaging Manufacturer
              </p>
            </div>
          </div>
        </div>

        {/* Metal Processing Card */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 dark:from-slate-900 dark:to-black p-8 rounded-2xl text-white border border-slate-700 dark:border-slate-800">
          <div className="text-emerald-400 dark:text-emerald-300 font-medium mb-1">
            Metal Processing
          </div>
          <div className="text-gray-400 dark:text-gray-500 text-sm mb-6">Australia</div>
          <h3 className="text-2xl font-bold mb-6 text-white">Engineering & Fabrication</h3>

          <div className="mb-6">
            <h4 className="font-semibold text-white mb-3">Challenge</h4>
            <p className="text-gray-300 dark:text-gray-400 text-sm leading-relaxed">
              High costs from an oversized and inefficient compressed air system.
            </p>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold text-white mb-3">ESA Solution</h4>
            <p className="text-gray-300 dark:text-gray-400 text-sm leading-relaxed">
              Upgrade of the compressed air system with a new VSD compressor and management
              system.
            </p>
          </div>

          <div className="border-t border-gray-700 dark:border-gray-800 pt-6">
            <h4 className="font-semibold text-emerald-400 dark:text-emerald-300 mb-4">
              Results Achieved
            </h4>
            <div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-400 dark:text-emerald-300 mb-2">
                  $21,000
                </div>
                <div className="text-sm text-gray-400 dark:text-gray-500 mb-3">
                  Annual Cost Savings
                </div>
                <div className="text-lg font-semibold text-emerald-300 dark:text-emerald-400">
                  Less than 2 years
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-600">Payback Period</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default RealResultsSection;


